<script setup>
import { computed } from "vue";

const props = defineProps({
    page: {
        type: Number,
        required: true,
    },
    pages: {
        type: Number,
        required: true,
    },
});

const emit = defineEmits(["update:page"]);

// Sliding window of up to five page numbers around the current page.
const windowPages = computed(() => {
    const start = Math.max(1, Math.min(props.page - 2, props.pages - 4));
    const end = Math.min(props.pages, start + 4);
    const numbers = [];

    for (let n = start; n <= end; n++) {
        numbers.push(n);
    }

    return numbers;
});

// Square hit area shared by the arrow and numbered buttons so every target
// is at least 40px; idle targets pick up the same subtle tint on hover.
const squareClass = "grid size-10 place-items-center rounded-[4px] transition";
const squareHoverClass =
    "enabled:hover:bg-brand-navy/10 enabled:hover:text-brand-red disabled:cursor-default disabled:text-[#9b9b9b]";

const edgeClass =
    "underline underline-offset-2 transition enabled:hover:text-brand-red disabled:cursor-default disabled:text-[#9b9b9b] disabled:no-underline";

// Pages exist beyond the window on that side; rendered as an ellipsis so the
// window never reads as the full range.
const hiddenBefore = computed(() => windowPages.value[0] > 1);
const hiddenAfter = computed(
    () => windowPages.value[windowPages.value.length - 1] < props.pages,
);

const ellipsisClass =
    "grid h-10 w-4 select-none place-items-center text-[#9b9b9b] sm:w-6";

function go(n) {
    if (n !== props.page) {
        emit("update:page", n);
    }
}
</script>

<template>
    <nav
        class="flex items-center justify-center gap-1 font-brand text-[16px] font-semibold text-brand-navy sm:gap-2"
        aria-label="Lists pagination"
    >
        <button
            type="button"
            :class="edgeClass"
            :disabled="page === 1"
            @click="go(1)"
        >
            First
        </button>
        <button
            type="button"
            :class="[squareClass, squareHoverClass]"
            :disabled="page === 1"
            aria-label="Previous page"
            @click="go(page - 1)"
        >
            <svg
                class="size-4"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
            >
                <path d="M10 3 5 8l5 5" />
            </svg>
        </button>
        <span
            v-if="hiddenBefore"
            :class="ellipsisClass"
            data-test="ellipsis-before"
            aria-hidden="true"
        >
            &hellip;
        </span>
        <button
            v-for="n in windowPages"
            :key="n"
            type="button"
            :class="[
                squareClass,
                n === page ? 'bg-brand-navy text-white' : squareHoverClass,
            ]"
            :aria-current="n === page ? 'page' : undefined"
            @click="go(n)"
        >
            {{ n }}
        </button>
        <span
            v-if="hiddenAfter"
            :class="ellipsisClass"
            data-test="ellipsis-after"
            aria-hidden="true"
        >
            &hellip;
        </span>
        <button
            type="button"
            :class="[squareClass, squareHoverClass]"
            :disabled="page === pages"
            aria-label="Next page"
            @click="go(page + 1)"
        >
            <svg
                class="size-4"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
            >
                <path d="m6 3 5 5-5 5" />
            </svg>
        </button>
        <button
            type="button"
            :class="edgeClass"
            :disabled="page === pages"
            @click="go(pages)"
        >
            Last
        </button>
    </nav>
</template>
